/**
 * LuxuryGiftCard — the standalone "Luxury Glass" digital gift card visual.
 *
 * Pure presentation: it receives already-sanitised public data (no Shopify
 * code, no PII, no ids) and renders it. Reused by the public /gift/:token page
 * and by the admin design preview with static fake data.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { formatGiftAmount, formatGiftDate } from '@/lib/giftPublic';

/** Official Riham logo, served from our own origin. */
export const BRAND_LOGO_SRC = '/riham-logo.png';



export interface LuxuryGiftCardData {
  headline: string;
  recipientName: string;
  senderName: string | null;
  message: string | null;
  occasion: string | null;
  initialAmount: number;
  /** Current balance, or null when Shopify could not be refreshed. */
  balanceAmount: number | null;
  balanceAvailable: boolean;
  balanceSyncedAt: string | null;
  currency: string;
  statusLabel: string;
  active: boolean;
  expiresOn: string | null;
  /** Last four characters only — never the full code. */
  lastCharacters: string | null;
  logoUrl: string | null;
  /** Canonical public page URL. The only value encoded in the QR. */
  qrValue: string;
  brandName?: string;
}

const MAX_TILT = 6; // degrees — deliberately capped and calm

function usePrefersReducedMotion(): boolean {
  const [reduced, setReduced] = useState(true);
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const apply = () => setReduced(mq.matches);
    apply();
    mq.addEventListener?.('change', apply);
    return () => mq.removeEventListener?.('change', apply);
  }, []);
  return reduced;
}

export function LuxuryGiftCard({ data }: { data: LuxuryGiftCardData }) {
  const reduced = usePrefersReducedMotion();
  const cardRef = useRef<HTMLDivElement>(null);
  const frame = useRef<number | null>(null);
  const target = useRef({ x: 0, y: 0 });

  const paint = useCallback(() => {
    frame.current = null;
    const el = cardRef.current;
    if (!el) return;
    el.style.setProperty('--tilt-x', `${target.current.y.toFixed(2)}deg`);
    el.style.setProperty('--tilt-y', `${target.current.x.toFixed(2)}deg`);
    el.style.setProperty('--glare-x', `${(50 + target.current.x * 4).toFixed(1)}%`);
    el.style.setProperty('--glare-y', `${(50 - target.current.y * 4).toFixed(1)}%`);
  }, []);

  const schedule = useCallback(() => {
    if (frame.current !== null) return;
    frame.current = requestAnimationFrame(paint);
  }, [paint]);

  useEffect(() => {
    if (reduced) return;
    const el = cardRef.current;
    if (!el) return;

    const onMove = (clientX: number, clientY: number) => {
      const rect = el.getBoundingClientRect();
      if (!rect.width || !rect.height) return;
      const nx = (clientX - rect.left) / rect.width - 0.5;
      const ny = (clientY - rect.top) / rect.height - 0.5;
      target.current = {
        x: Math.max(-1, Math.min(1, nx * 2)) * MAX_TILT,
        y: Math.max(-1, Math.min(1, ny * 2)) * MAX_TILT,
      };
      schedule();
    };
    const onPointer = (e: PointerEvent) => onMove(e.clientX, e.clientY);
    const onLeave = () => {
      target.current = { x: 0, y: 0 };
      schedule();
    };

    el.addEventListener('pointermove', onPointer, { passive: true });
    el.addEventListener('pointerleave', onLeave);
    el.addEventListener('pointercancel', onLeave);
    return () => {
      el.removeEventListener('pointermove', onPointer);
      el.removeEventListener('pointerleave', onLeave);
      el.removeEventListener('pointercancel', onLeave);
      if (frame.current !== null) cancelAnimationFrame(frame.current);
      frame.current = null;
    };
  }, [reduced, schedule]);

  // Official Riham brand logo, served from our own origin.
  const logoSrc = data.logoUrl ?? BRAND_LOGO_SRC;
  const expiry = formatGiftDate(data.expiresOn);
  const synced = formatGiftDate(data.balanceSyncedAt);

  return (
    <div className="lux-stage" data-reduced-motion={reduced ? 'true' : 'false'}>
      <div ref={cardRef} className="lux-card" role="group" aria-label={`${data.headline} · ${data.recipientName}`}>
        <div className="lux-aurora" aria-hidden="true" />
        <div className="lux-foil" aria-hidden="true" />
        <div className="lux-glare" aria-hidden="true" />
        <div className="lux-grain" aria-hidden="true" />
        <div className="lux-edge" aria-hidden="true" />

        <div className="lux-content">
          <header className="lux-head">
            {logoSrc ? (
              <img
                src={logoSrc}
                alt={data.brandName ?? 'Riham'}
                className="lux-logo"
                referrerPolicy="no-referrer"
                loading="lazy"
              />
            ) : (
              <span className="lux-wordmark">{data.brandName ?? 'RIHAM'}</span>
            )}
            <span className="lux-kicker">{data.headline}</span>
          </header>

          <dl className="lux-people">
            <div>
              <dt>לכבוד</dt>
              <dd className="lux-recipient">{data.recipientName}</dd>
            </div>
            {data.senderName && (
              <div>
                <dt>מאת</dt>
                <dd>{data.senderName}</dd>
              </div>
            )}
            {data.occasion && (
              <div>
                <dt>לרגל</dt>
                <dd>{data.occasion}</dd>
              </div>
            )}
          </dl>

          <div className="lux-amounts">
            <div>
              <dt className="lux-label">יתרה נוכחית</dt>
              <dd className="lux-balance" aria-live="polite">
                {data.balanceAvailable ? (
                  formatGiftAmount(data.balanceAmount, data.currency)
                ) : (
                  <span className="lux-balance-unavailable">היתרה אינה זמינה כרגע</span>
                )}
              </dd>
              {!data.balanceAvailable && (
                <p className="lux-note">
                  {synced ? `היתרה המוצגת עודכנה לאחרונה ב־${synced}. נסו לרענן בעוד רגע.` : 'לא הצלחנו לעדכן את היתרה מול המערכת. נסו לרענן בעוד רגע.'}
                </p>
              )}
            </div>
            <div className="lux-initial">
              <dt className="lux-label">סכום מקורי</dt>
              <dd>{formatGiftAmount(data.initialAmount, data.currency)}</dd>
            </div>
          </div>

          {data.message && <p className="lux-message">{data.message}</p>}

          <footer className="lux-foot">
            <dl className="lux-meta">
              <div>
                <dt>סטטוס</dt>
                <dd>{data.statusLabel}</dd>
              </div>
              <div>
                <dt>בתוקף עד</dt>
                <dd>{expiry ?? 'ללא תאריך תפוגה'}</dd>
              </div>
              <div>
                <dt>ספרות אחרונות</dt>
                <dd dir="ltr" className="lux-last4">
                  •••• {data.lastCharacters ?? '••••'}
                </dd>
              </div>
            </dl>

            <div className="lux-qr">
              <QRCodeSVG value={data.qrValue} size={78} level="M" fgColor="#1a0d14" bgColor="#f6f1ea" />
              <span className="sr-only">קוד סריקה לפתיחת עמוד השובר</span>
            </div>
          </footer>
        </div>
      </div>

      {!data.active && (
        <p className="lux-status-banner" role="status">
          השובר אינו פעיל לשימוש ({data.statusLabel})
        </p>
      )}
    </div>
  );
}

export default LuxuryGiftCard;
